import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";

import { Button, Descriptions, Form, Input, Modal, PageHeader, Table, Typography } from "antd";
import { LeftOutlined, PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import ReactQuill from 'react-quill';
import _ from "lodash"; //library used to encoding/decoding html entities

import notifier from "utils/notifier";
import { ErrorResponse } from "utils/types";
import { formatDate, handleDownload } from "utils/helpers";
import { UserRole, NoteAction } from "utils/enums";

import UploadDocumentModal from "components/Eligibility/MemberEligibilityDetails/UploadDocumentModal";
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import PostAcuteCareNotesDetailProps from "./Proptypes";

import "./index.scss";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";


const { Title } = Typography;


const PostAcuteCareNotesDetail = (props: PostAcuteCareNotesDetailProps) => {

  const history = useHistory();

  const [form] = Form.useForm();
  const [description, setDescription] = useState('');
  const [noteDateTime, setNoteDateTime] = useState('');
  const [noteBy, setNoteBy] = useState('');
  const [noteLogsOneDataSource, setNoteLogsOneDataSource] = useState<any[]>([]);
  const [noteLogsTwoDataSource, setNoteLogsTwoDataSource] = useState<any[]>([]);
  const [noteDocsDataSource, setNoteDocsDataSource] = useState<any[]>([]);
  const [isNoteLogVisible, setIsNoteLogVisible] = useState(false);
  const [isUploadDocumentModalVisible, setIsUploadDocumentModalVisible] = useState(false);
  const [dos, setDos] = useState(''); // placeholder to get and set dos from the upload document model

  useEffect(() => {
    getNoteDocs();
  }, []);

  const noteDocsColumns = [
    {
      title: "File Name",
      key: "RmiOrigFileName",
      dataIndex: "RmiOrigFileName",
      render: (_: string, record: any, __: number) =>
        <Button className="file-link" type="link" onClick={() => downloadMemberDocument(record)}>{record.RmiOrigFileName}</Button>,
      sorter: (a: any, b: any) => a.RmiOrigFileName.localeCompare(b.RmiOrigFileName)
    },
    {
      title: "Tags",
      key: "RmiDocumentTag",
      dataIndex: "RmiDocumentTag",
      sorter: (a: any, b: any) => a.RmiDocumentTag.localeCompare(b.RmiDocumentTag)
    },
    {
      title: "Upload By",
      key: "RmiUploadBy",
      dataIndex: "RmiUploadBy",
      sorter: (a: any, b: any) => a.RmiUploadBy.localeCompare(b.RmiUploadBy)
    },
    {
      title: "Upload Date",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (_: string, record: any, __: number) => moment(record.RmiUploadDate).format('MM/DD/YYYY [at] HH:mmA'),
      sorter: (a: any, b: any) => a.RmiUploadDate - b.RmiOrigFileName
    },
  ];

  const pacNoteHeaderLogsColumns = [
    {
      title: "Date",
      key: "RmiLogDate",
      dataIndex: "RmiLogDate",
      render: (_: string, record: any, __: number) => formatDate(record.RmiLogDate, 'MM/DD/YYYY')
    },
    {
      title: "Changed By",
      key: "RmiLogUser",
      dataIndex: "RmiLogUser",
    },
    {
      title: "Field",
      key: "RmiAttribute",
      dataIndex: "RmiAttribute",
      render: (_: string, record: any, __: number) => record.RmiAttribute.replace(/Rmi/g, '').replace(/([A-Z])/g, ' $1').trim()
    },
    {
      title: "Action",
      key: "RmiAction",
      dataIndex: "RmiAction",
      render: (_: string, record: any, __: number) => NoteAction[record.RmiAction]
    },
    {
      title: "New Value",
      key: "RmiNewValue",
      dataIndex: "RmiNewValue",
      render: (__: string, record: any, ___: number) => {
        return (
          <ReactQuill
            className="mt-2"
            theme={"bubble"}
            readOnly={true}
            value={_.unescape(record.RmiNewValue)}
          />
        )
      }
    },
    {
      title: "Old Value",
      key: "RmiOldValue",
      dataIndex: "RmiOldValue",
      render: (__: string, record: any, ___: number) => {
        return (
          <ReactQuill
            className="mt-2"
            theme={"bubble"}
            readOnly={true}
            value={_.unescape(record.RmiOldValue)}
          />
        )
      }
    },
  ];

  const pacNoteDocLogsColumns = [
    {
      title: "Date",
      key: "RmiLogDate",
      dataIndex: "RmiLogDate",
      render: (_: string, record: any, __: number) => formatDate(record.RmiLogDate, 'MM/DD/YYYY')
    },
    {
      title: "Change By",
      key: "RmiLogUser",
      dataIndex: "RmiLogUser",
    },
    {
      title: "Action",
      key: "RmiAction",
      dataIndex: "RmiAction",
      render: (_: string, record: any, __: number) => NoteAction[record.RmiAction.replace(/Rmi/g, '')]
    },
    {
      title: "Document ID",
      key: "RmiMemberDocumentId",
      dataIndex: "RmiMemberDocumentId",
    },
  ];

  //modules tools setup for the react-quill text editor
  const modules =  {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'background': [] }],
    ]
  }

  //formats setup for the react-quill text editor
  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'background'
  ];

  const getNoteDocs = () => {
    if (props.noteId === 0) { //noteId is 0, then it's creating a new note
      return;
    } else {
      return props.get(props.noteId)
        .then((response: any) => {
          setDescription(props.pacNote.RmiDescription);
          setNoteBy(props.pacNote.RmiNoteBy);
          setNoteDateTime(formatDate(props.pacNote.RmiNoteDate, 'MM/DD/YYYY HH:mm'));
          setNoteDocsDataSource(response);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(
            err.response?.data?.Message || "Something went wrong, please contact a system administrator."
          );
        });
    }
  }

  //create new and edit notes use the same save api
  const saveNote = () => {
    const formData = {
      "CompanyId": props.eligibilityMemberInfo.RmiCompanyId,
      "CreatedBy": props.pacNote.RmiCreatedBy ? props.pacNote.RmiCreatedBy : sessionStorage.user_id,
      "CreatedDate": props.pacNote.RmiCreatedDate? props.pacNote.RmiCreatedDate : moment(noteDateTime).toISOString(),
      "Description": _.escape(description),
      "MembMpiNo": props.eligibilityMemberInfo.RmiMembMpiNo,
      "MemberId": props.eligibilityMemberInfo.RmiMembId,
      "ModifiedBy": sessionStorage.user_id,
      "ModifiedDate": moment().toISOString(),
      "NoteBy": noteBy,
      "NoteDate": moment(noteDateTime).toISOString(),
      "NoteId": (props.pacNote.RmiNoteId !== 0) ? props.pacNote.RmiNoteId : 0
    }

    return props.saveNote(formData)
      .then(() => {
        notifier.success("Note saved!");
        switchToEligibilityDetailPage();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || "Something went wrong, please contact a system administrator.");
      });
  }

  //document too long to define
  const downloadMemberDocument = (document: any) => {
    return props.downloadMemberDocument(document.RmiFilePath, document.RmiFileName, document.RmiUid) //Need to test this. Legacy code is using RmiUid but it should be RmiMembMpiNo
      .then((response: any) => {
        handleDownload(response, document.RmiFileName);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const getNoteLogs = () => {
    if (props.noteId === 0) {
      return notifier.error("Note Id is not specified.");
    }
    return props.getNoteLogs(props.noteId)
      .then((response: any) => {
        setNoteLogsOneDataSource(response.RmiPacNoteHeaderLogs);
        setNoteLogsTwoDataSource(response.RmiPacNoteDocLogs);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message ||"Something went wrong, please contact a system administrator." );
      });
  };

  const openNoteLogModel = () => {
    setIsNoteLogVisible(true);
    getNoteLogs();
  };

  const print = () => {
    $(".print-logo").removeClass("exclude-print").addClass("include-print");
    window.print();
  }
  const closeNoteLogModal = () => {
    setIsNoteLogVisible(false);
  }

  const openUploadDocumentModal = () => {
    setIsUploadDocumentModalVisible(true);
  }

  const isUploadDocumentModalVisibleHandler = () => {
    setIsUploadDocumentModalVisible(false);
  }

  const switchToEligibilityDetailPage = () => {
    history.push({
      pathname: '/eligibility/member-detail',
      state: {
        eligibilityMemberInfo: props.eligibilityMemberInfo
      }
    });
  }

  return (
    <>
      <div className="printCareNotes mt-4">
        <hr />
        <PageHeader
          className="site-page-header"
          title="Post Acute Care Clinical Note"
          extra={[
            <Button
              type="primary"
              key="back"
              style={{ background: "orange", borderColor: "orange" }}
              onClick={() => switchToEligibilityDetailPage()}
              icon={<LeftOutlined />}
            >
              Back
            </Button>,

            <Button
              type="primary"
              key="print"
              onClick={print}
              icon={<PrinterOutlined />}
            >
              Print
            </Button>
          ]}
        >
          <Descriptions size="small" column={3}>
            <Descriptions.Item className="member-name" label="Member Name">{props.eligibilityMemberInfo.RmiRevFullName}</Descriptions.Item>
            <Descriptions.Item className="date-of-birth" label="Date of Birth">{formatDate(props.eligibilityMemberInfo.RmiBirth, 'MM/DD/YYYY')}</Descriptions.Item>
            <Descriptions.Item className="pcp" label="PCP">{props.primaryCareProvider.RmiLastName}, {props.primaryCareProvider.RmiFirstName}</Descriptions.Item>
            <Descriptions.Item className="gender" label="Gender"> {props.eligibilityMemberInfo.RmiSex}</Descriptions.Item>
            <Descriptions.Item className="age" label="Age">{moment().diff(props.eligibilityMemberInfo.RmiBirth, 'years')}</Descriptions.Item>
          </Descriptions>
        </PageHeader>

        <hr />

        <Form
          form={form}
          name='postAcuteCareNotesForm'
          onFinish={saveNote}
        >
          <div className="row flex-lg-wrap mb-3 ml-2 mr-2 mt-3">
            <div className="col-lg-24 col-md-24 mb-4">
              <div className="row">
                <div className="col">
                  <Form.Item className="note-by" label="Note By:">
                    <Input value={noteBy} onChange={(e) => setNoteBy(e.target.value)} disabled={sessionStorage.user_role !== UserRole.CASE}/>
                  </Form.Item>
                </div>
                <div className="col">
                  <Form.Item
                    className="note-date-time"
                    label="Note Date & Time:"
                  >
                    <DatePickerLocalised
                      date={noteDateTime}
                      setDate={setNoteDateTime}
                      formInstance={form}
                      formItemName={'noteDateTime'}
                      disabled={sessionStorage.user_role !== UserRole.CASE}
                      showTime={true}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="mb-4">
                <label className="editor-label">Description</label>
                <ReactQuill
                  className="mt-2"
                  theme={sessionStorage.user_role !== UserRole.CASE ? "bubble" : "snow"}
                  readOnly={sessionStorage.user_role !== UserRole.CASE}
                  modules={modules}
                  formats={formats}
                  value={_.unescape(description)}
                  onChange={(value) => setDescription(value)}
                />
              </div>

              <div className="mb-4">
                <Table
                  className="note-document-table"
                  columns={noteDocsColumns}
                  dataSource={noteDocsDataSource}
                  pagination={{ position: ["bottomCenter"],  hideOnSinglePage: true}}
                />
              </div>

              <Button
                type="primary"
                key="print"
                style={{ background: "orange", borderColor: "orange" }}
                onClick={() => switchToEligibilityDetailPage()}
                icon={<LeftOutlined />}
              >
                Back
              </Button>

              { sessionStorage.user_role === UserRole.CASE &&
                <>
                  <Button type="primary" className="mr-2" onClick={saveNote}>Save</Button>
                  <Button type="primary" className="mr-2" onClick={openUploadDocumentModal}>Upload Documents</Button>
                </>
              }
              { props.noteId > 0 &&
                <><Button className="ml-2" onClick={openNoteLogModel} icon={<SearchOutlined />}>Logs</Button></>
              }
            </div>
          </div>
        </Form>
      </div>
      <Modal
        visible={isNoteLogVisible}
        destroyOnClose={true}
        maskClosable={false}
        width={1400}
        onCancel={closeNoteLogModal}
        footer={null}
      >
        <Title className="text-center" level={3}>Post Acute Care Note Logs</Title>

        <label className="modal-label">Logs for the Current Note</label>
        <Table
          columns={pacNoteHeaderLogsColumns}
          dataSource={noteLogsOneDataSource}
          pagination={{ position: ["bottomCenter"],  hideOnSinglePage: true}}
          loading={props.isFetchingNoteLogs}
        />

        <label className="modal-label">Document Logs</label>
        <Table
          columns={pacNoteDocLogsColumns}
          dataSource={noteLogsTwoDataSource}
          pagination={{ position: ["bottomCenter"],  hideOnSinglePage: true}}
          loading={props.isFetchingNoteLogs}
        />
      </Modal>
      <UploadDocumentModal
        claimDos={dos}
        setClaimDos={setDos}
        getCategory={props.getCategory}
        uploadFilestoAuth={props.uploadFilestoAuth}
        eligibilityMemberInfo={props.eligibilityMemberInfo}
        isUploadDocumentModalVisibleHandler={isUploadDocumentModalVisibleHandler}
        isUploadDocumentModalVisible={isUploadDocumentModalVisible}
        isUploading={props.isUploading}
        uploadMultipleCategories={true}
        allowMultipleAttachments={true}
      />
    </>
  );
};

export default PostAcuteCareNotesDetail;
